// Considered: FilterChipsDisplayMolecule (plan OQ-4 ratified target) — rejected on inspection.
// Every prop lane (`propertyFilters`, `perspectiveFilters`, `relationshipFilters`, `arcFilters`) is
// UnifiedFilters-shaped; a composer session override (sphere/context/viewer) is not a filter, and
// forcing one through this molecule would mean synthesizing a fake `FilterExpression`. Overrides
// compose `ChipAtom` directly instead — the ratified "Override chips" reuse-without-modification
// target (spec Reuse Audit row) — the same pattern the lint chip strip already uses in
// `ComposerShellOrganism`.
import React from 'react';
import { useTranslation } from 'localization';
import type { Sphere } from 'types/global';
import PerspectiveMenu from '../PerspectiveMenu/PerspectiveMenu';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './TryBarMolecule.scss';

export type TryBarOverrideKind = 'sphere' | 'context' | 'viewer';

export interface TryBarOverrideChip {
  id: string;
  label: string;
  kind: TryBarOverrideKind;
}

export interface TryBarMoleculeProps {
  /** Viewer-as candidates. Empty renders the switcher disabled — see the anchor-sphere-picker gap note in ComposerShellOrganism. */
  perspectives: Sphere[];
  onViewerSelected: (perspectiveId: string) => void;
  /** Label of the perspective currently viewing-as, if any (undefined = viewing as self). */
  viewerLabel?: string;
  overrideChips: TryBarOverrideChip[];
  onClearOverride: (id: string) => void;
  onClearAll?: () => void;
  className?: string;
}

/**
 * Try mode's topstrip (SC4, SC5): viewer-as perspective switcher plus every active session
 * override (sphere/context/viewer) as a removable chip. Overrides live in `ComposerState.overrides`
 * (per-component) or, for viewer-as, in the caller's own Try-session state — this molecule only
 * renders whatever the caller already resolved into chip data.
 */
const TryBarMolecule: React.FC<TryBarMoleculeProps> = ({
  perspectives,
  onViewerSelected,
  viewerLabel,
  overrideChips,
  onClearOverride,
  onClearAll,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div className={['try-bar', className].filter(Boolean).join(' ')} data-testid="try-bar">
      <div className="try-bar__viewer">
        <span className="try-bar__viewer-label">
          {viewerLabel ? t('composer.tryMode.viewingAs', { name: viewerLabel }) : t('composer.tryMode.viewingAsSelf')}
        </span>
        {perspectives.length > 0 ? (
          <PerspectiveMenu perspectives={perspectives} perspectiveSelected={onViewerSelected} className="try-bar__viewer-menu" />
        ) : (
          <span className="try-bar__viewer-unavailable">{t('composer.tryMode.noPerspectives')}</span>
        )}
      </div>

      {overrideChips.length > 0 && (
        <div className="try-bar__overrides" data-testid="try-bar-overrides">
          {overrideChips.map((chip) => (
            <ChipAtom
              key={chip.id}
              label={chip.label}
              removable
              onRemove={() => {
                onClearOverride(chip.id);
                return false;
              }}
              className={`try-bar__chip try-bar__chip--${chip.kind}`}
            />
          ))}
          {onClearAll && (
            <ButtonAtom variant="text" size="small" onClick={onClearAll}>
              {t('composer.tryMode.clearAllOverrides')}
            </ButtonAtom>
          )}
        </div>
      )}
    </div>
  );
};

export default TryBarMolecule;
